/**
 * The browser's two clocks, bound to `ClockPort`.
 *
 * `performance.now()` and `Date.now()` measure different things and fail in
 * different ways, which is why the port exposes both and why this adapter reads
 * them as a pair. A suspended tab moves the wall clock and freezes the monotonic
 * one; an NTP correction moves the wall clock alone; a reload restarts the
 * monotonic timeline against a new origin. Every one of those is *detectable*
 * from `read()` plus `monotonicOrigin()`, and undetectable from either clock on
 * its own — a single-clock adapter would hand the engine a plausible number for
 * a device that was asleep, and nothing downstream could tell.
 *
 * Nothing here is feature-gated to a throw. A browser without `performance` gets
 * a wall-derived monotonic timeline that is clamped so it can never run
 * backwards; a browser whose `Intl` cannot resolve a zone gets `undefined`,
 * which is the answer the port defines for that case.
 */

import type { Milliseconds } from "@ff/domain";
import type { ClockPort, ClockReading, EpochMilliseconds } from "@ff/domain/ports";
import { epochMilliseconds } from "@ff/domain/ports";

/**
 * The shape of `performance` this adapter uses, and nothing more. Declared
 * structurally so the fallback path and the tests can supply a source without
 * standing up a whole `Performance`.
 */
export interface MonotonicTimeSource {
  /** Fractional milliseconds since {@link MonotonicTimeSource.timeOrigin}. */
  now(): number;
  /**
   * The wall instant the monotonic timeline started at. Absent on old engines
   * that shipped `performance.now()` before `timeOrigin` was specified.
   */
  readonly timeOrigin?: number | undefined;
}

export interface ClockEnvironment {
  /**
   * `undefined` when the platform has no monotonic clock at all. The adapter
   * then derives one from the wall clock, which is worse — it inherits every
   * jump — but a clamped, wrong-under-NTP timeline still beats refusing to run.
   */
  readonly monotonic: MonotonicTimeSource | undefined;
  readonly wallClock: () => number;
}

/** The globals, resolved once per construction so a test can substitute them. */
export function browserClockEnvironment(): ClockEnvironment {
  const candidate = globalThis.performance as MonotonicTimeSource | undefined;
  const monotonic =
    candidate !== undefined && typeof candidate.now === "function" ? candidate : undefined;
  return { monotonic, wallClock: () => Date.now() };
}

/**
 * Formatter options that pin every field this adapter reads back out.
 * `hourCycle: "h23"` matters: under `h12` or `h24` the hour part comes back as
 * `12` or `24` and the reconstructed instant lands half a day or one day away.
 */
const ZONE_PART_OPTIONS: Intl.DateTimeFormatOptions = {
  hourCycle: "h23",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
};

export class BrowserClock implements ClockPort {
  readonly #environment: ClockEnvironment;
  readonly #origin: EpochMilliseconds;
  /**
   * Only the wall-derived fallback can go backwards, but the clamp lives here
   * rather than in that branch because the guarantee — "the monotonic timeline
   * never decreases" — belongs to the port, not to one of its implementations.
   */
  #highWaterMonotonic: Milliseconds = 0;
  /**
   * Resolved formatters, and `null` for identifiers this platform rejected.
   * Caching the rejection matters as much as caching the success: an invalid
   * zone on a manual entry form is re-validated on every keystroke, and
   * constructing a formatter that throws is the expensive path.
   */
  readonly #zoneFormatters = new Map<string, Intl.DateTimeFormat | null>();

  constructor(environment: ClockEnvironment = browserClockEnvironment()) {
    this.#environment = environment;

    const declaredOrigin = environment.monotonic?.timeOrigin;
    this.#origin = epochMilliseconds(
      typeof declaredOrigin === "number" && Number.isFinite(declaredOrigin)
        ? declaredOrigin
        : // Without `timeOrigin` the origin is reconstructed once, at
          // construction, from the pair. Recomputing it later would let it drift
          // with the wall clock, and the whole use of this reading is that it is
          // a constant a checkpoint can be compared against. With no monotonic
          // source at all the fallback timeline starts at zero here, which is
          // what makes `origin + monotonic()` reconstruct the wall clock.
          environment.wallClock() - (environment.monotonic?.now() ?? 0),
    );
  }

  now(): EpochMilliseconds {
    return epochMilliseconds(this.#environment.wallClock());
  }

  monotonic(): Milliseconds {
    // Floored, not rounded: rounding can move a reading *forward* past the
    // instant it describes, and the engine subtracts these readings to decide
    // how much time it may charge to a segment.
    const reading = Math.floor(this.#rawMonotonic());
    if (reading > this.#highWaterMonotonic) this.#highWaterMonotonic = reading;
    return this.#highWaterMonotonic;
  }

  read(): ClockReading {
    // Monotonic first. The two reads cannot be atomic, so the gap between them
    // is charged somewhere no matter what — and reading monotonic first puts it
    // in the wall delta, where reconciliation treats it as elapsed time. The
    // other order would put it in the active-duration budget, which is the one
    // quantity that may never be inflated.
    const monotonic = this.monotonic();
    return { monotonic, wall: this.now() };
  }

  monotonicOrigin(): EpochMilliseconds {
    return this.#origin;
  }

  timeZone(): string {
    try {
      const zone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
      // Deliberately not cached: a device's zone changes when the user travels
      // or edits it, and a session that outlives the change would otherwise keep
      // stamping entries with a zone the device no longer has.
      if (typeof zone === "string" && zone.length > 0) return zone;
    } catch {
      // An engine without a resolvable zone still has to name one.
    }
    return "UTC";
  }

  resolvedOffsetSeconds(instant: EpochMilliseconds, timeZone: string): number | undefined {
    const formatter = this.#formatterFor(timeZone);
    if (formatter === undefined) return undefined;

    const parts = new Map<string, string>();
    for (const part of formatter.formatToParts(new Date(instant))) {
      parts.set(part.type, part.value);
    }

    const year = numericPart(parts, "year");
    const month = numericPart(parts, "month");
    const day = numericPart(parts, "day");
    const hour = numericPart(parts, "hour");
    const minute = numericPart(parts, "minute");
    const second = numericPart(parts, "second");
    if (
      year === undefined ||
      month === undefined ||
      day === undefined ||
      hour === undefined ||
      minute === undefined ||
      second === undefined
    ) {
      return undefined;
    }

    // The local wall reading, re-labelled as UTC. Its distance from the real
    // instant *is* the offset, which is the only way to get one out of `Intl`
    // without depending on a `timeZoneName` format that varies by engine.
    const projected = new Date(Date.UTC(2000, month - 1, day, hour, minute, second));
    // `Date.UTC` maps years 0–99 onto 1900–1999; the two-step assignment is the
    // documented way round it, and manual entry can legitimately reach them.
    projected.setUTCFullYear(year);

    // Compared against the *truncated* instant: the formatted parts stop at the
    // second, so leaving the caller's milliseconds in would push a zone that is
    // exactly on the hour off by one second and make the offset unusable as the
    // whole-minute value every consumer treats it as. `Math.floor` truncates the
    // same way the formatter does, on both sides of the epoch.
    const wholeSeconds = Math.floor(instant / 1_000) * 1_000;
    return Math.round((projected.getTime() - wholeSeconds) / 1_000);
  }

  #rawMonotonic(): number {
    const source = this.#environment.monotonic;
    if (source !== undefined) return source.now();
    // No monotonic clock: measure from the wall clock against the origin fixed
    // at construction. `monotonic()` clamps the result, so an NTP correction
    // that moves the wall backwards stalls this timeline instead of rewinding
    // it — a stall costs the user active time, a rewind would fabricate it.
    return this.#environment.wallClock() - this.#origin;
  }

  #formatterFor(timeZone: string): Intl.DateTimeFormat | undefined {
    if (this.#zoneFormatters.has(timeZone)) {
      return this.#zoneFormatters.get(timeZone) ?? undefined;
    }

    let formatter: Intl.DateTimeFormat | null = null;
    try {
      // `en-US` rather than the device locale: the parts are parsed back as
      // numbers, and a locale with non-ASCII digits would make that parse fail
      // on exactly the devices hardest to reproduce.
      formatter = new Intl.DateTimeFormat("en-US", { ...ZONE_PART_OPTIONS, timeZone });
    } catch {
      // An identifier this platform does not know. `undefined` is the port's
      // answer for it, and falling back to the device zone here would record an
      // entry against a zone the user did not choose.
      formatter = null;
    }
    this.#zoneFormatters.set(timeZone, formatter);
    return formatter ?? undefined;
  }
}

function numericPart(parts: ReadonlyMap<string, string>, type: string): number | undefined {
  const raw = parts.get(type);
  if (raw === undefined) return undefined;
  const value = Number.parseInt(raw, 10);
  return Number.isFinite(value) ? value : undefined;
}
